import { formatSaved, savedSeconds } from '@nolens/domain';
import { formatMonth } from '@nolens/i18n';
import { Select, useLocale } from '@nolens/ui';
import { componentsText } from '../i18n/components';

export type Period = 'all' | '3m' | '12m' | 'year';

/** «2026-10» está no período? (meses de calendário, hora local) */
export function inPeriod(month: string, p: Period, now = new Date()) {
  if (p === 'all') return true;
  const [y, m] = month.split('-').map(Number) as [number, number];
  if (p === 'year') return y === now.getFullYear();
  const diff = now.getFullYear() * 12 + now.getMonth() - (y * 12 + (m - 1));
  return diff < (p === '3m' ? 3 : 12);
}

export function PeriodSelect({
  value,
  onChange,
}: {
  value: Period;
  onChange: (p: Period) => void;
}) {
  const t = componentsText(useLocale());
  return (
    <div className="w-48">
      <Select
        aria-label={t.period}
        className="!h-9 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value as Period)}
      >
        <option value="all">{t.periodAll}</option>
        <option value="3m">{t.period3m}</option>
        <option value="12m">{t.period12m}</option>
        <option value="year">{t.periodYear}</option>
      </Select>
    </div>
  );
}

/** Linhas de poupança estimada (passos × 120 s), com barra e total. */
export function SavingsTable({
  rows,
  first,
  months = false,
}: {
  rows: { key: string; label: string; guides: number; steps: number }[];
  first: string;
  /** As etiquetas são meses «aaaa-mm». */
  months?: boolean;
}) {
  const locale = useLocale();
  const t = componentsText(locale);
  const monthLabel = (m: string) => {
    const [y, mo] = m.split('-').map(Number) as [number, number];
    return formatMonth(locale, y, mo);
  };
  if (!rows.length) return <p className="mt-3 text-text-2">{t.noGuidesInPeriod}</p>;
  const max = Math.max(1, ...rows.map((g) => g.steps));
  const steps = rows.reduce((n, r) => n + r.steps, 0);
  const guides = rows.reduce((n, r) => n + r.guides, 0);
  return (
    <div
      className="mt-3 overflow-x-auto rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]"
      data-savings-table
    >
      <table className="w-full text-[14px]">
        <thead className="text-left text-[12.5px] text-text-3">
          <tr>
            <th className="px-4 py-2.5 font-medium">{first}</th>
            <th className="px-4 py-2.5 font-medium">{t.guides}</th>
            <th className="px-4 py-2.5 font-medium">{t.steps}</th>
            <th className="w-[40%] px-4 py-2.5 font-medium">{t.estimatedSavings}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.key} className="border-t border-border">
              <td className="px-4 py-2.5 font-medium">{months ? monthLabel(g.label) : g.label}</td>
              <td className="px-4 py-2.5 tabular-nums">{g.guides}</td>
              <td className="px-4 py-2.5 tabular-nums">{g.steps}</td>
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-3">
                  <span
                    className="h-2 rounded-full bg-accent"
                    style={{ width: `${(g.steps / max) * 60}%` }}
                  />
                  <span className="tabular-nums">{formatSaved(savedSeconds(g.steps), locale)}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-border font-semibold">
            <td className="px-4 py-2.5">{t.total}</td>
            <td className="px-4 py-2.5 tabular-nums">{guides}</td>
            <td className="px-4 py-2.5 tabular-nums">{steps}</td>
            <td className="px-4 py-2.5">{formatSaved(savedSeconds(steps), locale)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
